import React, { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { BrainCircuit, ArrowRight, Sparkles, CheckCircle2, Loader2, Home, Clock, Target, Layers } from "lucide-react";

type Question = {
  id: string;
  title: string;
  subtitle: string;
  icon: React.ElementType;
  options: {
    label: string;
    description: string;
    scores: { standard: number; deep: number; moveOut: number };
  }[];
};

const QUESTIONS: Question[] = [
  {
    id: "intent",
    title: "¿Cuál es tu objetivo principal?",
    subtitle: "Ayúdanos a entender qué quieres lograr.",
    icon: Target,
    options: [
      { label: "Mantenimiento Rutinario", description: "Mantener la casa siempre limpia", scores: { standard: 3, deep: 0, moveOut: 0 } },
      { label: "Restauración Profunda", description: "Lidiar con la suciedad acumulada y áreas descuidadas", scores: { standard: 0, deep: 3, moveOut: 0 } },
      { label: "Mudanza", description: "Preparar una casa vacía para la transición", scores: { standard: 0, deep: 0, moveOut: 4 } },
    ],
  },
  {
    id: "condition",
    title: "¿Cómo describirías la acumulación de suciedad?",
    subtitle: "Sé honesto, lo hemos visto todo y no juzgamos.",
    icon: Layers,
    options: [
      { label: "Ligera / Polvo", description: "Necesita una buena limpieza y aspirado", scores: { standard: 2, deep: 0, moveOut: 0 } },
      { label: "Moderada", description: "Algo de espuma de jabón, grasa y pelo de mascotas", scores: { standard: 0, deep: 2, moveOut: 0 } },
      { label: "Pesada / Suciedad", description: "Acumulación significativa que requiere restregar", scores: { standard: 0, deep: 3, moveOut: 0 } },
      { label: "Casa Vacía", description: "Sin muebles, solo necesita un reinicio completo", scores: { standard: 0, deep: 0, moveOut: 2 } },
    ],
  },
  {
    id: "time",
    title: "¿Cuándo fue la última limpieza profunda profesional?",
    subtitle: "Esto nos ayuda a estimar el esfuerzo base requerido.",
    icon: Clock,
    options: [
      { label: "En los últimos 30 días", description: "Detallado recientemente", scores: { standard: 2, deep: 0, moveOut: 0 } },
      { label: "Hace 1-3 meses", description: "Empezando a acumular suciedad", scores: { standard: 1, deep: 1, moveOut: 0 } },
      { label: "Hace más de 3 meses", description: "Atrasado para un fregado detallado", scores: { standard: 0, deep: 2, moveOut: 0 } },
      { label: "Nunca / No estoy seguro", description: "Necesita un reinicio completo de base", scores: { standard: 0, deep: 3, moveOut: 0 } },
    ],
  },
  {
    id: "scope",
    title: "¿Cuál es el alcance de la limpieza?",
    subtitle: "Selecciona las áreas en las que quieres que nos enfoquemos.",
    icon: Home,
    options: [
      { label: "Áreas de Alto Tráfico", description: "Cocina, sala de estar, baños principales", scores: { standard: 2, deep: 0, moveOut: 0 } },
      { label: "Toda la Casa", description: "Cada habitación, de arriba a abajo", scores: { standard: 1, deep: 2, moveOut: 0 } },
      { label: "Reinicio Vacío", description: "Dentro de los gabinetes, electrodomésticos, todas las superficies", scores: { standard: 0, deep: 0, moveOut: 3 } },
    ],
  },
];

const PROCESSING_STEPS = [
  "Analizando la condición de la casa...",
  "Evaluando niveles de acumulación...",
  "Emparejando con alcances de servicio...",
  "Calculando puntuación de confianza...",
  "Generando recomendación final..."
];

export function SpanishServiceRecommendationEngine() {
  const [isOpen, setIsOpen] = useState(false);
  const [step, setStep] = useState(0);
  const [scores, setScores] = useState({ standard: 0, deep: 0, moveOut: 0 });
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingTextIndex, setProcessingTextIndex] = useState(0);
  const [result, setResult] = useState<{ package: string; confidence: number; reason: string } | null>(null);

  const [intervalId, setIntervalId] = useState<NodeJS.Timeout | null>(null);

  useEffect(() => {
    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [intervalId]);

  const handleOptionSelect = (optionScores: { standard: number; deep: number; moveOut: number }) => {
    const newScores = {
      standard: scores.standard + optionScores.standard,
      deep: scores.deep + optionScores.deep,
      moveOut: scores.moveOut + optionScores.moveOut,
    };
    setScores(newScores);

    if (step < QUESTIONS.length - 1) {
      setStep(step + 1);
    } else {
      startProcessing(newScores);
    }
  };

  const startProcessing = (finalScores: { standard: number; deep: number; moveOut: number }) => {
    setIsProcessing(true);
    setStep(QUESTIONS.length);

    let currentTextIndex = 0;
    const id = setInterval(() => {
      currentTextIndex++;
      if (currentTextIndex < PROCESSING_STEPS.length) {
        setProcessingTextIndex(currentTextIndex);
      } else {
        clearInterval(id);
        generateResult(finalScores);
      }
    }, 600);
    setIntervalId(id);
  };

  const generateResult = (finalScores: { standard: number; deep: number; moveOut: number }) => {
    let recommended = "Limpieza Estándar";
    let reason = "Esta recomendación se basa en tu objetivo de mantenimiento rutinario, niveles más ligeros de acumulación y horario de limpieza regular.";
    
    if (finalScores.moveOut > finalScores.standard && finalScores.moveOut > finalScores.deep) {
      recommended = "Limpieza de Mudanza";
      reason = "Esta recomendación se basa en tus necesidades de transición, un entorno de casa vacía y el requisito de un reinicio completo, incluyendo el interior de los gabinetes y electrodomésticos.";
    } else if (finalScores.deep > finalScores.standard) {
      recommended = "Limpieza Profunda";
      reason = "Esta recomendación se basa en el tiempo transcurrido desde tu última limpieza profesional, niveles moderados a pesados de acumulación y tu objetivo de restaurar profundamente la casa.";
    }

    const totalPoints = finalScores.standard + finalScores.deep + finalScores.moveOut;
    const maxPoints = Math.max(finalScores.standard, finalScores.deep, finalScores.moveOut);
    const calculatedConfidence = Math.min(98, Math.max(88, Math.round((maxPoints / totalPoints) * 100) + 15));

    setResult({
      package: recommended,
      confidence: calculatedConfidence,
      reason: reason,
    });
    setIsProcessing(false);
  };

  const reset = () => {
    setStep(0);
    setScores({ standard: 0, deep: 0, moveOut: 0 });
    setResult(null);
    setIsProcessing(false);
    setProcessingTextIndex(0);
  };

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      setTimeout(reset, 300);
    }
    setIsOpen(open);
  };

  return (
    <>
      {/* Container for the Recommendation Engine */}
      <Dialog open={isOpen} onOpenChange={handleOpenChange}>
<div className="flex justify-center w-full mt-16 mb-12 relative z-20 px-4 md:px-0">
          <DialogTrigger asChild>
            <div 
              role="button"
              tabIndex={0}
              className="group cursor-pointer relative overflow-hidden rounded-3xl bg-gradient-to-r from-primary to-[#0073a8] dark:from-primary dark:to-[#004766] text-primary-foreground shadow-xl hover:shadow-2xl hover:-translate-y-1 transition-all duration-300 h-auto w-full max-w-5xl mx-auto flex flex-col md:flex-row items-stretch border-4 border-white/10 dark:border-white/5 outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 whitespace-normal text-left"
            >
              {/* Decorative background effects */}
              <div className="absolute inset-0 w-full h-full bg-[linear-gradient(45deg,transparent_25%,rgba(255,255,255,0.1)_50%,transparent_75%)] bg-[length:250%_250%,100%_100%] animate-[shimmer_3s_linear_infinite] opacity-30"></div>
              <div className="absolute -right-20 -top-20 w-64 h-64 bg-white/10 rounded-full blur-3xl group-hover:bg-white/20 transition-colors duration-500"></div>
              
              <div className="flex flex-col items-start justify-center p-6 md:p-10 lg:p-12 text-left z-10 flex-1 w-full min-w-0">
                <span className="inline-flex items-center justify-center px-4 py-1.5 rounded-full bg-white/20 text-white text-xs md:text-sm font-bold uppercase tracking-wider mb-4 border border-white/30 backdrop-blur-sm">
                  <BrainCircuit className="mr-2 h-4 w-4 shrink-0" /> <span className="whitespace-nowrap">AI Matchmaker</span>
                </span>
                <span className="text-3xl md:text-4xl lg:text-5xl font-black tracking-tight mb-3 text-white drop-shadow-md leading-tight break-words whitespace-normal w-full">
                  ¿Necesitas Ayuda para Elegir?
                </span>
                <span className="text-base md:text-lg lg:text-xl font-medium text-white/90 max-w-2xl leading-relaxed whitespace-normal break-words">
                  Toma nuestro rápido cuestionario para encontrar el paquete de limpieza perfecto para tu hogar y presupuesto.
                </span>
              </div>

              <div className="p-6 md:p-10 lg:p-12 shrink-0 flex items-center justify-center z-10 bg-black/10 md:bg-black/5 border-t border-white/10 md:border-t-0 md:border-l w-full md:w-auto">
                <div className="inline-flex items-center justify-center bg-primary-foreground text-primary rounded-2xl px-6 py-4 md:px-10 md:py-6 font-bold text-lg md:text-xl shadow-lg group-hover:scale-105 group-hover:shadow-xl transition-all duration-300 whitespace-nowrap">
                  Encontrar mi paquete
                  <ArrowRight className="ml-3 h-5 w-5 md:h-6 md:w-6 group-hover:translate-x-1.5 transition-transform shrink-0" />
                </div>
              </div>
            </div>
          </DialogTrigger>
        </div>

        <DialogContent className="sm:max-w-[600px] p-0 overflow-hidden bg-background border-border/50 shadow-2xl rounded-3xl">
          <DialogTitle className="sr-only">Motor de Recomendación de Servicio</DialogTitle>
          
          {!result && !isProcessing && (
            <div className="w-full bg-secondary h-1.5">
              <div 
                className="bg-primary h-full transition-all duration-500 ease-out"
                style={{ width: `${((step + 1) / QUESTIONS.length) * 100}%` }}
              />
            </div>
          )}

          <div className="p-6 sm:p-10 min-h-[400px] flex flex-col relative">
            
            {!isProcessing && !result && step < QUESTIONS.length && (
              <div className="flex-1 flex flex-col animate-in fade-in duration-500">
                <div className="mb-8">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="p-2.5 bg-primary/10 rounded-xl text-primary">
                      {(() => {
                        const Icon = QUESTIONS[step].icon;
                        return <Icon className="w-6 h-6" />;
                      })()}
                    </div>
                    <span className="text-sm font-bold tracking-wider uppercase text-primary/80">Paso {step + 1} de {QUESTIONS.length}</span>
                  </div>
                  <h2 className="text-3xl font-extrabold text-foreground mb-2 leading-tight tracking-tight">
                    {QUESTIONS[step].title}
                  </h2>
                  <p className="text-muted-foreground text-lg">
                    {QUESTIONS[step].subtitle}
                  </p>
                </div>

                <div className="grid gap-3 mt-auto">
                  {QUESTIONS[step].options.map((option, idx) => (
                    <button
                      key={idx}
                      onClick={() => handleOptionSelect(option.scores)}
                      className="group flex flex-col items-start p-5 rounded-2xl border-2 border-border/60 bg-card hover:border-primary hover:bg-primary/5 transition-all duration-200 text-left"
                    >
                      <span className="font-bold text-lg text-foreground group-hover:text-primary transition-colors">
                        {option.label}
                      </span>
                      <span className="text-sm text-muted-foreground mt-1">
                        {option.description}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {isProcessing && (
              <div className="flex-1 flex flex-col items-center justify-center text-center animate-in fade-in duration-500 py-12">
                <div className="relative mb-8">
                  <div className="absolute inset-0 bg-primary/20 rounded-full blur-xl animate-pulse"></div>
                  <div className="bg-card border border-border p-5 rounded-full relative z-10 shadow-lg">
                    <BrainCircuit className="w-12 h-12 text-primary animate-pulse" />
                  </div>
                </div>
                <h3 className="text-2xl font-bold text-foreground mb-3">Análisis de IA en Progreso</h3>
                <div className="h-6 overflow-hidden relative w-full max-w-[250px]">
                  {PROCESSING_STEPS.map((text, idx) => (
                    <div 
                      key={idx}
                      className={`absolute inset-0 transition-all duration-500 flex items-center justify-center text-muted-foreground font-medium
                        ${idx === processingTextIndex ? 'opacity-100 translate-y-0' : 
                          idx < processingTextIndex ? 'opacity-0 -translate-y-full' : 'opacity-0 translate-y-full'}`}
                    >
                      {text}
                    </div>
                  ))}
                </div>
                <Progress value={(processingTextIndex / (PROCESSING_STEPS.length - 1)) * 100} className="h-1.5 w-48 mt-8" />
              </div>
            )}

            {result && (
              <div className="flex-1 flex flex-col animate-in slide-in-from-bottom-4 fade-in duration-500">
                <div className="flex items-center justify-between mb-8">
                  <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 text-sm font-bold border border-emerald-500/20">
                    <Sparkles className="w-4 h-4" />
                    Análisis Completo
                  </div>
                  <div className="text-right">
                    <div className="text-3xl font-black text-primary tracking-tighter">{result.confidence}%</div>
                    <div className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">Puntuación de Coincidencia</div>
                  </div>
                </div>

                <div className="bg-secondary/50 rounded-3xl p-8 border border-border mb-8">
                  <p className="text-sm font-bold uppercase tracking-widest text-muted-foreground mb-2">Paquete Recomendado</p>
                  <h2 className="text-4xl font-black text-foreground mb-4 tracking-tight">
                    {result.package}
                  </h2>
                  <div className="flex items-start gap-3">
                    <CheckCircle2 className="w-6 h-6 text-primary shrink-0 mt-0.5" />
                    <p className="text-muted-foreground leading-relaxed text-lg">
                      {result.reason}
                    </p>
                  </div>
                </div>

                <div className="mt-auto pt-4">
                  <Button 
                    className="w-full h-16 text-lg font-bold rounded-2xl shadow-lg hover:shadow-xl transition-all hover:-translate-y-1"
                    onClick={() => {
                      setIsOpen(false);
                      const serviceMap: Record<string, "standard" | "deep" | "move"> = {
                        "Limpieza Estándar": "standard",
                        "Limpieza Profunda": "deep",
                        "Limpieza de Mudanza": "move"
                      };
                      const recommendedService = serviceMap[result.package] || "standard";
                      
                      try {
                        const raw = localStorage.getItem("uplevel_calc_state");
                        const current = raw ? JSON.parse(raw) : {};
                        const next = { ...current, serviceType: recommendedService };
                        localStorage.setItem("uplevel_calc_state", JSON.stringify(next));
                        window.dispatchEvent(new CustomEvent("uplevel_calc_sync", { detail: next }));
                      } catch (e) {}

                      window.location.href = "/book?view=custom-plan";
                    }}
                  >
                    Continuar a la Reserva
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                  <Button
                    variant="ghost"
                    className="w-full mt-2 text-muted-foreground hover:text-foreground transition-all duration-300 hover:bg-secondary/50 hover:scale-[1.02] animate-in fade-in slide-in-from-bottom-2 duration-500 delay-300"
                    onClick={reset}
                  >
                    Volver a tomar el cuestionario
                  </Button>
                  <p className="text-center text-sm text-muted-foreground mt-4">
                    Toma menos de 60 segundos. No se requiere tarjeta de crédito para reservar la fecha.
                  </p>
                </div>
              </div>
            )}

          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
